import { Dispatch, SetStateAction, useState } from 'react';

/**
 * Reflejo local del cursor de Redux, SIN el render de retraso.
 *
 * ── El problema que resuelve ───────────────────────────────────────────────
 *
 * Las 9 tablas guardaban el cursor de Redux en un estado local y lo
 * sincronizaban con un efecto:
 *
 *     const [selectorCursor, setSelectorCursor] = useState(globalCursor);
 *     useEffect(() => { setSelectorCursor(globalCursor); }, [globalCursor]);
 *
 * Un efecto corre DESPUÉS de pintar, así que `selectorCursor` iba **un render
 * por detrás** de `globalCursor`. Mientras los filtros eran no controlados
 * (`defaultValue`) daba igual: nadie leía el valor viejo. Con los filtros
 * multi-selección sí se nota, porque `MultiSelectDropdown` copia la prop
 * `selectedValues` a su estado interno cada vez que cambia:
 *
 *   1. Marcas una opción → el hijo se pinta con ella (estado propio) y la
 *      tabla despacha el cursor nuevo a Redux.
 *   2. La tabla se vuelve a pintar porque Redux cambió, pero `selectorCursor`
 *      TODAVÍA es el viejo → le pasa al hijo `states ?? []`, o sea un array
 *      vacío recién creado → el hijo lo copia y **se desmarca**.
 *   3. Corre el efecto, `selectorCursor` alcanza a Redux → el hijo recibe el
 *      valor bueno y **se vuelve a marcar**.
 *
 * Entre (2) y (3) el navegador pinta, y eso es el parpadeo: la pastilla y la
 * casilla se apagan y se encienden (~50 ms, medido con MutationObserver en
 * el listado de Órdenes de trabajo). En el formulario de Nueva orden no pasa
 * porque allí el valor no viene de un reflejo con retraso.
 *
 * ── El arreglo ─────────────────────────────────────────────────────────────
 *
 * Ajustar el estado DURANTE el render en vez de en un efecto — el patrón que
 * documenta React para «reajustar estado cuando cambia una prop». React
 * descarta el render en curso y repite el del componente con el valor nuevo
 * **antes de tocar el DOM y antes de pintar a los hijos**, así que el valor
 * viejo no llega a verse. La semántica es la misma que tenía el efecto (cada
 * vez que cambia la identidad de `globalCursor`, el reflejo se reemplaza);
 * lo único que desaparece es el render intermedio.
 *
 * Devuelve la misma tupla que `useState` para poder sustituirlo en el sitio:
 * las tablas siguen escribiendo el reflejo a mano (`onClearValue`,
 * autoselección de concesión…) entre cambios de Redux.
 *
 * ── Por qué esto y no otra cosa ────────────────────────────────────────────
 *
 * React documenta tres salidas al «estado derivado de una prop», por orden de
 * preferencia: (1) reiniciar todo el estado con `key`, (2) calcularlo en el
 * render sin guardarlo, (3) ajustarlo durante el render. Aquí:
 *
 *   1. `key` no sirve: reiniciaría también el estado de los hijos — el
 *      desplegable abierto se cerraría en cada cambio de filtro.
 *   2. Calcularlo (o sea, leer `globalCursor` y borrar el reflejo) es lo ideal,
 *      pero HOY no es equivalente: `AccidentsTable` y `CheckBoxTagTable`
 *      escriben el reflejo **sin despachar** en la autoselección de concesión,
 *      así que el reflejo guarda estado que Redux no tiene. Borrarlo cambiaría
 *      su comportamiento. Queda como limpieza aparte: hacer que esas dos
 *      despachen como las otras ocho y retirar el reflejo entero.
 *   3. Es esto. La documentación de `useState` lo describe exactamente:
 *      «React volverá a renderizar ese componente inmediatamente después de
 *      que salga con un `return`, y ANTES de renderizar a los hijos», que es
 *      justo lo que impide que el valor viejo se pinte, y añade que «es mejor
 *      que actualizar el estado en un efecto».
 *
 * ⚠️ Riesgo conocido y MEDIDO: facebook/react#25565 («useSyncExternalStore
 * dropped update when state is dispatched in render phase») se arregló en
 * React 19 y este proyecto va en 18.2 con `useSelector`, que lee el store con
 * `useSyncExternalStoreWithSelector`. Sobre el papel es el escenario del
 * fallo. No se reproduce aquí: lo comprueba
 * `tests/unit/tables/mirrored-cursor-external-store.spec.tsx` con el stack
 * real. Si esos casos se ponen en rojo, este hook deja de ser seguro.
 */
export function useMirroredCursor<T>(
  globalCursor: T
): [T, Dispatch<SetStateAction<T>>] {
  const [cursor, setCursor] = useState<T>(globalCursor);
  // Última identidad de `globalCursor` ya reflejada. Compara por referencia a
  // propósito: `useSelector(getCursor(id), isEqual)` ya garantiza que solo
  // cambia cuando cambia el contenido.
  const [mirrored, setMirrored] = useState<T>(globalCursor);

  if (mirrored !== globalCursor) {
    setMirrored(globalCursor);
    setCursor(globalCursor);
  }

  return [cursor, setCursor];
}
